import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { createContextScope } from '@radix-ui/react-context';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Primitive } from '@radix-ui/react-primitive';
import * as TogglePrimitive from '@radix-ui/react-toggle';
import { useCallbackRef } from '@radix-ui/react-use-callback-ref';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { createCollectionIndex } from '../CollectionIndex/index.js';
/**
 * TODO: Add see more/less logic
 */
/* -------------------------------------------------------------------------------------------------
 * FacetValueList
 * ----------------------------------------------------------------------------------------------- */
const FACET_VALUE_LIST_NAME = 'FacetValueList';
const [CollectionIndex, useCollectionIndex, createCollectionIndexScope] = createCollectionIndex(FACET_VALUE_LIST_NAME);
const FACET_VALUE_ITEM_NAME = 'FacetValueListItem';
const [createFacetItemContext, createContextFacetItemScope] = createContextScope(FACET_VALUE_ITEM_NAME);
const [createFacetValueListContext, createContextFacetValueListScope] = createContextScope(FACET_VALUE_LIST_NAME, [
    createCollectionIndexScope,
    createContextFacetItemScope,
]);
const [FacetValueListProvider, useFacetValueListContext] = createFacetValueListContext(FACET_VALUE_LIST_NAME);
const FacetValueList = React.forwardRef(({ __scopeFacetValueList, facetId, onFacetClick, orientation = 'vertical', ...props }, forwardedRef) => {
    return (_jsx(CollectionIndex.Provider, { scope: __scopeFacetValueList, children: _jsx(FacetValueListProvider, { scope: __scopeFacetValueList, onFacetClick: onFacetClick, facetId: facetId, children: _jsx(CollectionIndex.Slot, { scope: __scopeFacetValueList, children: _jsx(Primitive.ul, { "data-orientation": orientation, ...props, ref: forwardedRef }) }) }) }));
});
FacetValueList.displayName = FACET_VALUE_LIST_NAME;
const Root = FacetValueList;
const [FacetValueListItemProvider, useFacetValueListItemContext] = createFacetItemContext(FACET_VALUE_ITEM_NAME);
/**
 * This component should be use inside FacetValueList
 */
const FacetValueListItem = React.forwardRef((props, forwardedRef) => {
    const { __scopeFacetValueList, defaultSelected, facetValueId, selected: selectedProp, ...facetItemProps } = props;
    const { facetId, onFacetClick = () => { } } = useFacetValueListContext(FACET_VALUE_ITEM_NAME, __scopeFacetValueList);
    const ref = React.useRef(null);
    const composedRef = useComposedRefs(forwardedRef, ref);
    const getIndex = useCollectionIndex(ref, __scopeFacetValueList);
    const onFacetItemClickHandler = useCallbackRef((selected) => {
        onFacetClick({
            facetId,
            facetValueId,
            facetValueIndex: getIndex(),
            checked: selected,
        });
    });
    const [selected = false, setChecked] = useControllableState({
        prop: selectedProp,
        defaultProp: defaultSelected,
        onChange: onFacetItemClickHandler,
    });
    return (_jsx(FacetValueListItemProvider, { scope: __scopeFacetValueList, onFacetItemChange: setChecked, selected: selected, id: facetValueId, children: _jsx(CollectionIndex.ItemSlot, { scope: __scopeFacetValueList, children: _jsx(Primitive.li, { "data-state": selected ? 'selected' : 'unselected', ref: composedRef, ...facetItemProps }) }) }));
});
FacetValueListItem.displayName = FACET_VALUE_ITEM_NAME;
/* -------------------------------------------------------------------------------------------------
 * FacetValueListItemLabel
 * ----------------------------------------------------------------------------------------------- */
/**
 * This component should be use inside FacetItem
 */
const FACET_VALUE_ITEM_LABEL_NAME = 'FacetValueListItemLabel';
const FacetValueListItemLabel = React.forwardRef((props, forwardedRef) => {
    const { __scopeFacetValueList, ...labelProps } = props;
    const { id } = useFacetValueListItemContext(FACET_VALUE_ITEM_LABEL_NAME, __scopeFacetValueList);
    return _jsx(LabelPrimitive.Root, { htmlFor: id, ...labelProps, ref: forwardedRef });
});
FacetValueListItemLabel.displayName = FACET_VALUE_ITEM_LABEL_NAME;
/* -------------------------------------------------------------------------------------------------
 * FacetValueListItemCheckbox
 * ----------------------------------------------------------------------------------------------- */
/**
 * This component should be use inside FacetItem
 */
const FACET_VALUE_ITEM_CHECKBOX_NAME = 'FacetValueListItemCheckbox';
const FacetValueListItemCheckbox = React.forwardRef((props, forwardedRef) => {
    const { __scopeFacetValueList, ...checkboxProps } = props;
    const { id, selected, onFacetItemChange } = useFacetValueListItemContext(FACET_VALUE_ITEM_CHECKBOX_NAME, __scopeFacetValueList);
    return (_jsx(CheckboxPrimitive.Root, { checked: selected, id: id, onCheckedChange: onFacetItemChange, ref: forwardedRef, ...checkboxProps }));
});
FacetValueListItemCheckbox.displayName = FACET_VALUE_ITEM_CHECKBOX_NAME;
/* -------------------------------------------------------------------------------------------------
 * FacetValueListItemCheckboxIndicator
 * ----------------------------------------------------------------------------------------------- */
/**
 * This component should contain the icon to be displayed for the checkbox when it is checked
 */
const FacetValueListItemCheckboxIndicator = CheckboxPrimitive.Indicator;
FacetValueListItemCheckboxIndicator.displayName = 'FacetValueListItemCheckboxIndicator';
/* -------------------------------------------------------------------------------------------------
 * FacetItemToggle
 * ----------------------------------------------------------------------------------------------- */
/**
 * This component should be use inside FacetValueListItem
 */
const FACET_VALUE_ITEM_TOGGLE_NAME = 'FacetValueListItemToggle';
const FacetValueListItemToggle = React.forwardRef((props, forwardedRef) => {
    const { __scopeFacetValueList, ...toggleProps } = props;
    const { id, selected, onFacetItemChange } = useFacetValueListItemContext(FACET_VALUE_ITEM_TOGGLE_NAME, __scopeFacetValueList);
    return (_jsx(TogglePrimitive.Root, { pressed: selected, id: id, onPressedChange: onFacetItemChange, ref: forwardedRef, ...toggleProps }));
});
FacetValueListItemToggle.displayName = FACET_VALUE_ITEM_TOGGLE_NAME;
const Item = FacetValueListItem;
const Checkbox = FacetValueListItemCheckbox;
const CheckboxIndicator = FacetValueListItemCheckboxIndicator;
const Label = FacetValueListItemLabel;
const Toggle = FacetValueListItemToggle;
export { createContextFacetValueListScope, createContextFacetItemScope, 
//
FacetValueList, FacetValueListItem, FacetValueListItemLabel, FacetValueListItemCheckbox, FacetValueListItemCheckboxIndicator, FacetValueListItemToggle, 
//
Root, Item, Label, Checkbox, CheckboxIndicator, Toggle, };
